'use client';

import { useState } from 'react';
import type { CurrentUserHrContext } from '@/app/hr/components/types';
import PtoRequestModal from './PtoRequestModal';

interface MeTimeOffTabProps {
  ctx: CurrentUserHrContext;
  onRequestSubmitted: () => void;
}

const STATUS_BADGES: Record<string, { bg: string; text: string; label: string }> = {
  pending:   { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'PENDING' },
  approved:  { bg: 'bg-green-100',  text: 'text-green-700',  label: 'APPROVED' },
  denied:    { bg: 'bg-red-100',    text: 'text-red-700',    label: 'DENIED' },
  cancelled: { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'CANCELLED' },
};

const BALANCE_LABELS: Record<string, { label: string; color: string }> = {
  pto:               { label: 'Paid Time Off',    color: '#E89A3C' },
  sick:              { label: 'Sick Time',        color: '#dc2626' },
  bereavement:       { label: 'Bereavement',      color: '#6b7280' },
  jury_duty:         { label: 'Jury Duty',        color: '#7c3aed' },
  parental:          { label: 'Parental Leave',   color: '#16a34a' },
  floating_holiday:  { label: 'Floating Holiday', color: '#F5A623' },
};

const MeTimeOffTab = ({ ctx, onRequestSubmitted }: MeTimeOffTabProps) => {
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Available Balances</h3>
          <button
            type="button"
            onClick={() => setRequestModalOpen(true)}
            className="px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
          >
            + Request Time Off
          </button>
        </div>

        {ctx.timeOffBalances.length === 0 ? (
          <p className="text-[12px] text-gray-400 italic">No balances configured. Contact HR.</p>
        ) : (
          <div className="space-y-3">
            {ctx.timeOffBalances.map(bal => {
              const cfg = BALANCE_LABELS[bal.balance_type] ?? { label: bal.balance_type, color: '#6b7280' };
              const pctUsed = bal.hours_accrued > 0 ? (bal.hours_used / bal.hours_accrued) * 100 : 0;
              return (
                <div key={bal.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-[13px] font-bold text-[#1C2E5E]">{cfg.label}</p>
                      <p className="text-[10px] text-gray-500">{bal.hours_per_year ? `${bal.hours_per_year}h/yr accrual` : 'Standard accrual'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: cfg.color }}>{bal.hours_remaining.toFixed(1)}</p>
                      <p className="text-[10px] text-gray-500">hours remaining</p>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden mb-1">
                    <div className="h-full transition-all" style={{ width: `${Math.min(100, pctUsed)}%`, background: cfg.color }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span>{bal.hours_used.toFixed(1)} used</span>
                    <span>{bal.hours_accrued.toFixed(1)} total</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Request History</h3>
        {ctx.ptoRequests.length === 0 ? (
          <p className="text-[12px] text-gray-400 italic">No requests yet. Submit one above.</p>
        ) : (
          <div className="space-y-2">
            {ctx.ptoRequests.map(req => {
              const cfg = STATUS_BADGES[req.status] ?? STATUS_BADGES.cancelled;
              const balCfg = BALANCE_LABELS[req.balance_type] ?? { label: req.balance_type, color: '#6b7280' };
              const dateLabel = req.start_date === req.end_date
                ? new Date(req.start_date).toLocaleDateString()
                : `${new Date(req.start_date).toLocaleDateString()} – ${new Date(req.end_date).toLocaleDateString()}`;

              return (
                <div key={req.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <p className="text-[12px] font-bold text-[#1C2E5E]">
                        {req.hours_requested}h {balCfg.label}
                      </p>
                      <p className="text-[11px] text-gray-500">{dateLabel}</p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${cfg.bg} ${cfg.text}`}>
                      {cfg.label}
                    </span>
                  </div>
                  {req.notes && <p className="text-[11px] text-gray-600 italic mt-1">&ldquo;{req.notes}&rdquo;</p>}
                  {req.resolution_notes && (
                    <p className="text-[11px] mt-1.5">
                      <span className="text-[9px] uppercase tracking-wide font-bold text-gray-500">Manager response: </span>
                      {req.resolution_notes}
                    </p>
                  )}
                  {req.resolved_by_name && req.resolved_at && (
                    <p className="text-[10px] text-gray-400 mt-1">By {req.resolved_by_name} on {new Date(req.resolved_at).toLocaleDateString()}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <PtoRequestModal
        open={requestModalOpen}
        employee={ctx.employee}
        balances={ctx.timeOffBalances}
        onClose={() => setRequestModalOpen(false)}
        onSubmitted={() => {
          onRequestSubmitted();
        }}
      />
    </div>
  );
};

export default MeTimeOffTab;
